import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESEARCHER_OPTIONS = [
  { value: "independent",    label: "Independent Research" },
  { value: "academic",       label: "Academic / University Research" },
  { value: "pharmaceutical", label: "Pharmaceutical Research" },
  { value: "clinical",       label: "Clinical Laboratory" },
  { value: "veterinary",     label: "Veterinary Research" },
  { value: "other",          label: "Other Scientific Research" },
];

/**
 * Landing Request Access form.
 *   POST /api/access-request  →  saves the request + emails ADMIN_NOTIFY_EMAIL.
 * On success the form is replaced with an in-place thank-you card.
 * Anchor id="request-access" is used by the landing CTAs to scroll here.
 */
export default function LandingRequestAccessSection() {
  const nav = useNavigate();
  const location = useLocation();
  const { refresh: refreshAuth } = useAuth();
  const nameInputRef = useRef(null);
  const formCardRef = useRef(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    institution: "",
    researcher_type: "",
    message: "",
    referred_by: "",
    invite_code: "",
    password: "",
    subscribe_to_mailing_list: true,  // iter_134 — default on so we don't ghost the 10% incentive
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [fastTracked, setFastTracked] = useState(false);

  // iter_141 — when a visitor arrives with #request-access in the hash
  // (from the /enter "New here? Request access" link, or any of the landing
  // CTAs), scroll the form card into view and auto-focus the Full name
  // input so the sign-up box is unambiguously the next action.
  useEffect(() => {
    if (location.hash !== "#request-access") return;
    if (submitted) return;
    // Wait one frame so refs are mounted, then a bit more so any layout
    // shifts from other sections finish.
    const t = setTimeout(() => {
      if (formCardRef.current) {
        formCardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      // Focus after the smooth scroll settles.
      setTimeout(() => nameInputRef.current?.focus({ preventScroll: true }), 550);
    }, 60);
    return () => clearTimeout(t);
  }, [location.hash, submitted]);

  const hasInvite = form.invite_code.trim().length > 0;
  const canSubmit =
    form.name.trim() &&
    /^\S+@\S+\.\S+$/.test(form.email.trim()) &&
    form.researcher_type &&
    (!hasInvite || form.password.length >= 8);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/access-request`, {
        name: form.name.trim(),
        email: form.email.trim(),
        institution: form.institution.trim(),
        researcher_type: form.researcher_type,
        message: form.message.trim(),
        referred_by: form.referred_by.trim(),
        invite_code: form.invite_code.trim().toUpperCase(),
        password: hasInvite ? form.password : "",
        subscribe_to_mailing_list: Boolean(form.subscribe_to_mailing_list),
      }, { withCredentials: true });
      // Instant-access path: backend already set auth cookies + returned the user.
      if (data?.instant_access) {
        await refreshAuth();
        nav(data.user?.role === "admin" ? "/admin" : "/chooser", { replace: true });
        return;
      }
      setFastTracked(Boolean(data?.fast_track));
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      id="request-access"
      className="relative bg-white border-t border-borderSubtle py-20 sm:py-28 lg:py-32 scroll-mt-24"
      data-testid="landing-request-access"
      aria-label="Request access to the research catalog"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-20 items-start">
          {/* Left — copy */}
          <div>
            <p className="text-sm sm:text-base uppercase tracking-[0.32em] text-oxblood font-mono">
              Apply for membership
            </p>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-charcoal tracking-tight leading-[1.02]">
              Request access to the <span className="italic text-charcoal/85">catalog.</span>
            </h2>
            <p className="mt-6 max-w-[46ch] text-base sm:text-lg text-charcoal/85 leading-relaxed">
              Every account is reviewed before pricing is unlocked. Tell us a little
              about your work; approval usually comes back the same day, and often
              within the hour.
            </p>

            <ul className="mt-10 space-y-4 text-base sm:text-lg text-charcoal">
              {[
                "Verified researchers only — no drop-shipping resellers.",
                "Your details are used solely to approve access, never sold.",
                "Already approved? Use the sign-in link at the top.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="mt-1 block w-6 h-px bg-oxblood shrink-0" aria-hidden />
                  <span className="text-charcoal/90">{t}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right — form or thank-you state */}
          {submitted ? <ThankYouCard email={form.email.trim()} fastTracked={fastTracked} /> : (
            <form
              ref={formCardRef}
              onSubmit={submit}
              className="relative bg-bone border border-borderSubtle rounded-[6px] p-8 sm:p-10 space-y-5 scroll-mt-24"
              data-testid="landing-access-form"
              noValidate
            >
              <CornerMarks />

              <FieldGrid>
                <Field label="Full name" required>
                  <input
                    ref={nameInputRef}
                    type="text"
                    autoComplete="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputCls}
                    data-testid="access-name-input"
                    required
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className={inputCls}
                    data-testid="access-email-input"
                    required
                  />
                </Field>
              </FieldGrid>

              <Field label="Institution / Company">
                <input
                  type="text"
                  autoComplete="organization"
                  value={form.institution}
                  onChange={(e) => setForm({ ...form, institution: e.target.value })}
                  className={inputCls}
                  placeholder="Optional — leave blank if independent"
                  data-testid="access-institution-input"
                />
              </Field>

              <Field label="Researcher type" required>
                <select
                  value={form.researcher_type}
                  onChange={(e) => setForm({ ...form, researcher_type: e.target.value })}
                  className={`${inputCls} appearance-none pr-8 bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2210%22 height=%226%22 viewBox=%220 0 10 6%22><path d=%22M1 1l4 4 4-4%22 stroke=%22%23555%22 stroke-width=%221.25%22 fill=%22none%22/></svg>')] bg-no-repeat bg-[right_0.75rem_center]`}
                  data-testid="access-researcher-type"
                  required
                >
                  <option value="" disabled>Select your researcher type…</option>
                  {RESEARCHER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </Field>

              <Field label="Message (optional)">
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className={`${inputCls} resize-y min-h-[6rem]`}
                  placeholder="What kind of work are you doing? Which kits are you interested in?"
                  data-testid="access-message-input"
                  maxLength={1000}
                />
              </Field>

              <Field label="Referred by (optional)">
                <input
                  type="text"
                  value={form.referred_by}
                  onChange={(e) => setForm({ ...form, referred_by: e.target.value })}
                  className={inputCls}
                  placeholder="How did you hear about us? (name, site, podcast, etc.)"
                  data-testid="access-referred-by-input"
                  maxLength={200}
                />
              </Field>

              {/* Invite code — quiet, secondary, only researchers with a code know to fill it */}
              <div className="pt-3 border-t border-borderSubtle">
                <Field label="Invite code (optional)">
                  <input
                    type="text"
                    value={form.invite_code}
                    onChange={(e) => setForm({ ...form, invite_code: e.target.value.toUpperCase() })}
                    className={`${inputCls} font-mono tracking-[0.12em] uppercase`}
                    placeholder="E.g. SARA-2026"
                    data-testid="access-invite-code-input"
                    maxLength={60}
                  />
                </Field>
                <p className="mt-2 text-[13px] uppercase tracking-[0.24em] text-charcoal/90">
                  {hasInvite
                    ? "Set a password below and you'll get instant access — no email round-trip."
                    : "Got a code from a trusted researcher? A valid code fast-tracks you past manual review."}
                </p>
              </div>

              {/* Password — appears only when an invite code is being used */}
              {hasInvite && (
                <div className="pt-3 border-t border-borderSubtle animate-fade-up">
                  <Field label="Create a password">
                    <input
                      type="password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className={inputCls}
                      placeholder="At least 8 characters"
                      autoComplete="new-password"
                      data-testid="access-password-input"
                      minLength={8}
                      maxLength={128}
                    />
                  </Field>
                  <p className="mt-2 text-[13px] uppercase tracking-[0.24em] text-charcoal/90">
                    You'll be signed in the moment you submit.
                  </p>
                </div>
              )}

              {error && (
                <p className="text-xs text-oxblood" data-testid="access-form-error">{error}</p>
              )}

              {/* iter_134 — mailing-list opt-in tied to 10% welcome code */}
              <label
                className="flex items-start gap-3 cursor-pointer group border-t border-borderSubtle pt-4"
                data-testid="access-subscribe-label"
              >
                <input
                  type="checkbox"
                  checked={Boolean(form.subscribe_to_mailing_list)}
                  onChange={(e) => setForm({ ...form, subscribe_to_mailing_list: e.target.checked })}
                  className="mt-1 w-4 h-4 rounded-none border border-charcoal/60 text-oxblood focus:ring-0 focus:ring-offset-0 accent-oxblood cursor-pointer"
                  data-testid="access-subscribe-checkbox"
                />
                <span className="text-[14px] text-charcoal/90 group-hover:text-charcoal transition-colors leading-relaxed">
                  Email me about restocks, new kits, and occasional sales. One-click unsubscribe in every email.
                </span>
              </label>

              <div className="pt-3 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4 border-t border-borderSubtle">
                <p className="text-[13px] uppercase tracking-[0.24em] text-charcoal/90 font-mono">
                  Reviewed by a human · usually same day
                </p>
                <button
                  type="submit"
                  disabled={!canSubmit || busy}
                  className="group inline-flex items-center justify-center gap-3 rounded-full bg-oxblood text-bone px-8 py-4 text-[14px] uppercase tracking-[0.22em] font-semibold hover:bg-charcoal transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto shadow-sm"
                  data-testid="access-submit-btn"
                >
                  {busy ? "Submitting…" : "Submit request"}
                  <span className="grid place-items-center w-7 h-7 rounded-full bg-bone/20 group-hover:translate-x-0.5 transition-transform" aria-hidden>
                    <ArrowRight size={15} strokeWidth={2.25} />
                  </span>
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

const inputCls =
  "w-full mt-2 h-11 px-3 bg-white border border-borderSubtle rounded-[4px] text-[15px] text-charcoal placeholder:text-charcoal/70 focus:outline-none focus:border-oxblood transition-colors";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[12px] uppercase tracking-[0.28em] text-charcoal font-mono font-semibold">
        {label}
        {required && <span className="text-oxblood ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}

function FieldGrid({ children }) {
  return <div className="grid sm:grid-cols-2 gap-5">{children}</div>;
}

function ThankYouCard({ email, fastTracked }) {
  return (
    <div
      className="relative bg-bone border border-borderSubtle rounded-[6px] p-8 sm:p-12 min-h-[440px] flex flex-col justify-center"
      data-testid="landing-access-thankyou"
    >
      <CornerMarks />
      <div className="w-14 h-14 rounded-full grid place-items-center bg-oxblood/10">
        <CheckCircle2 size={26} strokeWidth={1.5} className="text-oxblood" />
      </div>
      <p className="mt-8 text-[13px] uppercase tracking-[0.32em] text-oxblood font-mono">
        {fastTracked ? "You're in" : "Request received"}
      </p>
      {fastTracked ? (
        <>
          <h3 className="mt-4 font-serif text-3xl sm:text-4xl text-charcoal tracking-tight leading-[1.05]">
            Your invite has been <span className="italic">accepted.</span>
          </h3>
          <p className="mt-5 text-sm sm:text-base text-stone leading-relaxed max-w-[42ch]">
            Check <span className="font-mono text-charcoal">{email}</span> — we've
            sent a link to set your password. That's all you need. See you inside.
          </p>
        </>
      ) : (
        <>
          <h3 className="mt-4 font-serif text-3xl sm:text-4xl text-charcoal tracking-tight leading-[1.05]">
            Thanks — we'll be in touch <span className="italic">shortly.</span>
          </h3>
          <p className="mt-5 text-sm sm:text-base text-stone leading-relaxed max-w-[42ch]">
            We've recorded your application and pinged the team. You'll hear back at{" "}
            <span className="font-mono text-charcoal">{email}</span> — usually the same
            day, often within the hour.
          </p>
        </>
      )}
      <div className="mt-8 pt-5 border-t border-borderSubtle text-[12px] uppercase tracking-[0.28em] text-stone font-mono">
        Nothing else you need to do right now.
      </div>
    </div>
  );
}

function CornerMarks() {
  const stroke = "rgba(26,26,26,0.22)";
  const size = 10;
  const Mark = ({ style }) => (
    <span className="absolute" style={style} aria-hidden>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <path d={`M0 0 H${size} M0 0 V${size}`} stroke={stroke} strokeWidth="1" />
      </svg>
    </span>
  );
  return (
    <>
      <Mark style={{ top: 10, left: 10 }} />
      <Mark style={{ top: 10, right: 10, transform: "scaleX(-1)" }} />
      <Mark style={{ bottom: 10, left: 10, transform: "scaleY(-1)" }} />
      <Mark style={{ bottom: 10, right: 10, transform: "scale(-1,-1)" }} />
    </>
  );
}
